import React, { useState, useEffect } from 'react'
import { X, Settings, Zap, Save, RotateCcw, Rocket } from 'lucide-react'
import { useProviders } from '../hooks/useProviders'
import { useAdminMode } from '../hooks/useAdminMode'
import type { Provider } from '../services/chatService'

interface ZenSettingsProps {
  isOpen: boolean
  onClose: () => void
}

interface ZenSettings {
  defaultProvider: Provider | null
  autoSaveEnabled: boolean
  debugMode: boolean
  diffAnalysisEnabled: boolean
  autoDetectLanguage: boolean
  theme: 'zen' | 'minimal' | 'focus'
  aiResponseSpeed: 'balanced' | 'fast' | 'quality'
}

const defaultSettings: ZenSettings = {
  defaultProvider: null,
  autoSaveEnabled: true,
  debugMode: false,
  diffAnalysisEnabled: false,
  autoDetectLanguage: true,
  theme: 'zen',
  aiResponseSpeed: 'fast'
}

export const ZenSettingsModal: React.FC<ZenSettingsProps> = ({
  isOpen,
  onClose
}) => {
  const { providers, currentProvider } = useProviders()
  const isAdmin = useAdminMode()
  const [settings, setSettings] = useState<ZenSettings>(defaultSettings)
  const [hasChanges, setHasChanges] = useState(false)

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('zenCreatorSettings')
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings)
        setSettings({ ...defaultSettings, ...parsed })
      } catch (error) {
        console.warn('Failed to parse saved settings:', error)
      }
    } else if (currentProvider) {
      // Set current provider as default if no settings exist
      setSettings(prev => ({ ...prev, defaultProvider: currentProvider }))
    }
  }, [currentProvider])

  // Track changes
  useEffect(() => {
    const savedSettings = localStorage.getItem('zenCreatorSettings')
    const currentSettingsString = JSON.stringify(settings)
    const savedSettingsString = savedSettings || JSON.stringify(defaultSettings)
    setHasChanges(currentSettingsString !== savedSettingsString)
  }, [settings])

  const handleSave = () => {
    localStorage.setItem('zenCreatorSettings', JSON.stringify(settings))
    setHasChanges(false)

    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('zenSettingsChanged'))

    onClose()
  }

  const handleReset = () => {
    setSettings(defaultSettings)
    localStorage.removeItem('zenCreatorSettings')
    setHasChanges(false)
  }

  const handleProviderChange = (provider: Provider) => {
    setSettings(prev => ({ ...prev, defaultProvider: provider }))
  }

  const getCerebrasModels = () => {
    return providers.filter(p => 
      p.status?.includes('Cerebras') || 
      p.status?.includes('Ultra-Fast')
    )
  }

  const getStandardModels = () => {
    return providers.filter(p => 
      !p.status?.includes('Cerebras') && 
      !p.status?.includes('Ultra-Fast')
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl mx-4 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <Settings className="w-6 h-6 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Zen Creator Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* AI Provider Settings - Admin Only */}
          {isAdmin && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                <Zap className="w-5 h-5 text-blue-500" />
                <span>AI Provider & Model</span>
              </h3>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Default Provider & Model
              </label>
              
              {/* Cerebras Models Section */}
              {getCerebrasModels().length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm font-medium text-orange-600">
                    <Rocket className="w-4 h-4" />
                    <span>Ultra-Fast Models (Cerebras)</span>
                  </div>
                  <div className="grid grid-cols-1 gap-2 pl-6">
                    {getCerebrasModels().map((provider) => (
                      <label key={`${provider.provider}-${provider.model}`} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="defaultProvider"
                          checked={
                            settings.defaultProvider?.provider === provider.provider &&
                            settings.defaultProvider?.model === provider.model
                          }
                          onChange={() => handleProviderChange(provider)}
                          className="text-orange-600 focus:ring-orange-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {provider.provider} • {provider.model}
                          </div>
                          <div className="text-xs text-orange-600 font-medium">
                            {provider.status}
                          </div>
                        </div>
                        <Rocket className="w-3 h-3 text-orange-500" />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Standard Models Section */}
              {getStandardModels().length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-600">
                    Standard Models
                  </div>
                  <div className="grid grid-cols-1 gap-2 pl-6">
                    {getStandardModels().map((provider) => (
                      <label key={`${provider.provider}-${provider.model}`} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="defaultProvider"
                          checked={
                            settings.defaultProvider?.provider === provider.provider &&
                            settings.defaultProvider?.model === provider.model
                          }
                          onChange={() => handleProviderChange(provider)}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {provider.provider} • {provider.model}
                          </div>
                          <div className="text-xs text-gray-500">
                            {provider.status}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {providers.length === 0 && (
                <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-md">
                  No providers available. Please check your configuration.
                </div>
              )}
            </div>
            </div>
          )}

          {/* AI Response Speed - Admin Only */}
          {isAdmin && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Response Optimization</h3>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Preferred Response Speed
              </label>
              <div className="space-y-2">
                {[
                  { value: 'fast', label: 'Ultra-Fast', desc: 'Prioritize Cerebras models for 200-800ms responses' },
                  { value: 'balanced', label: 'Balanced', desc: 'Balance speed and model capability' },
                  { value: 'quality', label: 'Quality', desc: 'Prioritize best models regardless of speed' }
                ].map((option) => (
                  <label key={option.value} className="flex items-start space-x-3">
                    <input
                      type="radio"
                      name="aiResponseSpeed"
                      value={option.value}
                      checked={settings.aiResponseSpeed === option.value}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        aiResponseSpeed: e.target.value as ZenSettings['aiResponseSpeed']
                      }))}
                      className="mt-1 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            </div>
          )}

          {/* Zen Experience */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Zen Experience</h3>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Interface Theme
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'zen', label: 'Zen', desc: 'Meditative, nature-inspired' },
                  { value: 'minimal', label: 'Minimal', desc: 'Clean, distraction-free' },
                  { value: 'focus', label: 'Focus', desc: 'High-contrast, productivity' }
                ].map((theme) => (
                  <label key={theme.value} className="flex flex-col items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="theme"
                      value={theme.value}
                      checked={settings.theme === theme.value}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        theme: e.target.value as ZenSettings['theme']
                      }))}
                      className="mb-2 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="text-sm font-medium text-gray-900">{theme.label}</div>
                    <div className="text-xs text-gray-500 text-center">{theme.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Auto-save work</label>
                  <p className="text-xs text-gray-500">Automatically save your progress</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.autoSaveEnabled}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    autoSaveEnabled: e.target.checked
                  }))}
                  className="text-blue-600 focus:ring-blue-500"
                />
              </div>

              {/* Debug Mode - Admin Only */}
              {isAdmin && (
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Debug mode</label>
                    <p className="text-xs text-gray-500">Show technical details and performance metrics</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.debugMode}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      debugMode: e.target.checked
                    }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Diff Analysis - Admin Only */}
              {isAdmin && (
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <span>🧪 Syntax Diff Analysis</span>
                      <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Experimental</span>
                    </label>
                    <p className="text-xs text-gray-500">Enable side-by-side syntax comparison between languages</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.diffAnalysisEnabled}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      diffAnalysisEnabled: e.target.checked
                    }))}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                </div>
              )}

              {/* Language Detection */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <span>🔍 Auto-detect Language</span>
                  </label>
                  <p className="text-xs text-gray-500">Warn when detected language doesn't exactly match your selection</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.autoDetectLanguage}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    autoDetectLanguage: e.target.checked
                  }))}
                  className="text-blue-600 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleReset}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset to defaults</span>
          </button>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>Save Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ZenSettingsModal